import type { FC, ReactNode } from 'react'
import type { WashPackage } from '@/types'

import { Info, Phone } from 'lucide-react'
import { buttonStyles } from '@/lib/button-styles'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { PackageCard } from '@/components/landing/package-card'
import { business } from '@/constants/business'

interface PackagesSectionProps {
    packages: Array<WashPackage>
}

export const PackagesSection: FC<PackagesSectionProps> = ({ packages }): ReactNode => {
    return (
        <section id='packages' aria-labelledby='packages-title' className='scroll-mt-20 bg-background py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='packages-title'
                    eyebrow='Prices'
                    title='Pick your clean'
                    description="Every wash starts with snow foam and ends with a careful hand dry. Choose how far you'd like to go in between."
                />

                {packages.length > 0 ? (
                    <div className='mt-16 grid gap-6 sm:grid-cols-2 xl:grid-cols-4'>
                        {packages.map(pkg => (
                            <PackageCard key={pkg.id} pkg={pkg} />
                        ))}
                    </div>
                ) : (
                    <div className='mt-14 rounded-[1.75rem] border border-dashed border-input bg-foam p-10 text-center'>
                        <p className='font-heading text-3xl font-bold uppercase text-ink'>
                            Our price list is being updated
                        </p>

                        <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                            Give us a call and we'll talk you through today's packages and prices.
                        </p>

                        <a href={business.phone.href} className={buttonStyles({ variant: 'dark', className: 'mt-6' })}>
                            <Phone className='size-4' />
                            {business.phone.display}
                        </a>
                    </div>
                )}

                <p className='mt-12 flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground'>
                    <Info className='mt-0.5 size-4 shrink-0 text-aqua-deep' />
                    Prices are for small and medium cars. Larger cars, SUVs, vans and 7-seaters can cost a little more, and we always confirm the price before we start.
                </p>
            </Container>
        </section>
    )
}
